<template>
  <zm-button type="text" class="opc-btn-icon__wrapper" :class="{'opc-btn-icon__wrapper--spaceless': spaceless}" v-bind="attrs">
    <zm-icon
      :class="iconClass"
      :aria-label="ariaLabel">
      <component :is="iconComponent" />
    </zm-icon>
  </zm-button>
</template>

<script setup>
import { computed, useAttrs, watchEffect } from 'vue';
import { logger } from '@/utils/log';
import { ZmIcon } from '@zoom/zoom-ui-vue3';
import {
  InformationSmall as Information,
  InformationSmall,
  InformationFill,
  InformationSmallFill,
  CheckmarkCircle,
  CheckmarkCircleSmall,
  CheckmarkCircleFill,
  CheckmarkCircleSmallFill,
  AlertCircle,
  AlertCircleSmall,
  AlertCircleFill,
  AlertCircleSmallFill,
  ManualCloseMark,
  ManualCloseMarkFill,
} from '@zoom/icons-vue';

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();
const hasAccessibleLabel = computed(() => Boolean(attrs['aria-label'] || attrs['aria-labelledby']));

if (process.env.NODE_ENV !== 'production') {
  watchEffect(() => {
    if (!hasAccessibleLabel.value) {
      logger.warn('[tips-icon] Missing aria-label or aria-labelledby.');
    }
  });
}

// Define props
const props = defineProps({
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['info', 'success', 'warning', 'manualClose'].includes(value),
  },
  size: {
    type: String,
    default: 'default',
    validator: (value) => ['small', 'default'].includes(value),
  },
  fill: {
    type: Boolean,
    default: false,
  },
  spaceless: {
    type: Boolean,
    default: false,
  },
});

// Icon component mapping
const iconMap = {
  info: {
    default: Information,
    small: InformationSmall,
    defaultFill: InformationFill,
    smallFill: InformationSmallFill,
  },
  success: {
    default: CheckmarkCircle,
    small: CheckmarkCircleSmall,
    defaultFill: CheckmarkCircleFill,
    smallFill: CheckmarkCircleSmallFill,
  },
  warning: {
    default: AlertCircle,
    small: AlertCircleSmall,
    defaultFill: AlertCircleFill,
    smallFill: AlertCircleSmallFill,
  },
  manualClose: {
    default: ManualCloseMark,
    defaultFill: ManualCloseMarkFill,
  }
};

// Get the icon component based on type, size and fill
const iconComponent = computed(() => {
  const icons = iconMap[props.type] || iconMap.info;

  if (props.fill) {
    const fillKey = props.size === 'small' ? 'smallFill' : 'defaultFill';
    return icons[fillKey] || icons.defaultFill;
  }

  return icons[props.size] || icons.default;
});

// Generate CSS class for styling
const iconClass = computed(() => {
  const fillClass = props.fill ? 'tips-icon--fill' : '';
  return `tips-icon--${props.type} tips-icon--${props.size} ${fillClass}`.trim();
});

// Generate aria-label based on type
const ariaLabel = computed(() => {
  const labels = {
    info: 'Information',
    success: 'Success',
    warning: 'Warning',
  };
  return labels[props.type] || 'Information';
});
</script>

<style lang="scss" scoped>
.opc-btn-icon__wrapper {
  color: unset !important;
  transform: translateY(-1px);
  margin-left: 4px;
  &--spaceless{
    margin-left: 0;
  }

  :deep(.zbo-button__label) {
    width: 14px;
    height: 14px;
  }
}

.tips-icon--info {
}

.tips-icon--success {
}

.tips-icon--warning {
}

.tips-icon--error {
}

.tips-icon--small {
}

.tips-icon--default {
}

.tips-icon--fill {
  // Add styles for fill variant if needed
}
</style>

